if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.previewTimer = null;
      this.hoverPreviewEnabled = window.matchMedia("(hover: hover) and (pointer: fine)");
    }

    get selectedOptionValues() {
      const options = this.querySelectorAll("select, .variant-picker__field input[type='radio']");
      const selectedValues = new Map();

      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;
        if (field.tagName.toLowerCase() === "select") {
          const selectedOption = Array.from(field.options).find((option) => option.selected && !option.disabled && option.value);
          if (selectedOption) selectedValues.set(fieldName, selectedOption.dataset.optionValueId);
        } else if (field.checked) {
          selectedValues.set(fieldName, field.dataset.optionValueId);
        }
      });

      return Array.from(selectedValues.values()).filter(Boolean);
    }

    getInputForEventTarget(target) {
      return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
    }

    connectedCallback() {
      this.injectPeakstyleStyles();
      if (this.dataset.peakstyleEventsBound === "true") return;
      this.dataset.peakstyleEventsBound = "true";

      this.addEventListener("change", (event) => {
        const target = this.getInputForEventTarget(event.target);
        if (!target || !window.FoxThemeEvents || typeof PUB_SUB_EVENTS === "undefined") return;
        window.FoxThemeEvents.emit(PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: { event, target, selectedOptionValues: this.selectedOptionValues },
        });
      });

      this.addEventListener("mouseover", this.handleColorPreviewEnter.bind(this));
      this.addEventListener("mouseout", this.handleColorPreviewLeave.bind(this));
    }

    injectPeakstyleStyles() {
      if (document.getElementById("PeakstyleVariantPickerStyles")) return;
      const style = document.createElement("style");
      style.id = "PeakstyleVariantPickerStyles";
      style.textContent = `
        .variant-picker__color .variant-picker__option-button--color,
        .variant-picker__color .variant-picker__option-button--swatch {
          width: 4.6rem;
          height: 4.6rem;
          border-radius: var(--btn-rounded, 0);
          background-size: contain;
          background-position: center;
          background-repeat: no-repeat;
          background-color: var(--color-bg-1, #fff);
        }

        @media (max-width: 767.98px) {
          .template-product .f-product-single__blocks,
          .template-product .f-product-single__block,
          .template-product .f-product__description,
          .template-product .f-product-single__block--custom_liquid,
          .template-product .f-product-single__block--richtext,
          .template-product .collapsible__content-inner,
          .template-product .rte {
            min-width: 0;
            max-width: 100%;
            box-sizing: border-box;
            overflow-wrap: anywhere;
            word-break: normal;
          }

          .template-product .f-product__description ul,
          .template-product .f-product__description ol,
          .template-product .f-product-single__block--custom_liquid ul,
          .template-product .f-product-single__block--custom_liquid ol,
          .template-product .f-product-single__block--richtext ul,
          .template-product .f-product-single__block--richtext ol,
          .template-product .collapsible__content-inner ul,
          .template-product .collapsible__content-inner ol,
          .template-product .rte ul,
          .template-product .rte ol {
            width: auto;
            max-width: 100%;
            margin-inline: 0;
            padding-inline-start: 2.2rem;
            box-sizing: border-box;
            list-style-position: outside;
          }

          .template-product .f-product__description li,
          .template-product .f-product-single__block--custom_liquid li,
          .template-product .f-product-single__block--richtext li,
          .template-product .collapsible__content-inner li,
          .template-product .rte li {
            max-width: 100%;
            padding-inline-start: 0.3rem;
            overflow-wrap: anywhere;
          }

          .template-product .f-product-single__blocks img,
          .template-product .f-product-single__blocks video,
          .template-product .f-product-single__blocks iframe,
          .template-product .f-product-single__blocks svg,
          .template-product .f-product-single__blocks table {
            max-width: 100%;
          }

          .template-product .f-product-single__blocks table {
            display: block;
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .template-product .f-product-single__blocks pre,
          .template-product .f-product-single__blocks code {
            white-space: pre-wrap;
            overflow-wrap: anywhere;
          }
        }
      `;
      document.head.appendChild(style);
    }

    normalizeSaleBadge() {
      const productInfo = this.closest("product-info");
      const badge = productInfo?.querySelector('[id^="Price-"] .f-price__badge-sale .f-badge');
      if (!badge) return;
      const text = badge.textContent.trim();
      const match = text.match(/-?\s*(\d+(?:[.,]\d+)?)\s*%/);
      if (match) badge.textContent = `Spare ${match[1]}%`;
    }

    handleColorPreviewEnter(event) {
      if (!this.hoverPreviewEnabled.matches) return;
      const label = event.target.closest(".variant-picker__color .variant-picker__option-button");
      if (!label || label.contains(event.relatedTarget)) return;
      const input = label.previousElementSibling;
      if (!input || input.tagName !== "INPUT") return;
      window.clearTimeout(this.previewTimer);
      this.previewTimer = window.setTimeout(() => {
        this.previewVariantImage(input);
        window.setTimeout(() => this.normalizeSaleBadge(), 0);
      }, 60);
    }

    handleColorPreviewLeave(event) {
      if (!this.hoverPreviewEnabled.matches) return;
      const label = event.target.closest(".variant-picker__color .variant-picker__option-button");
      if (!label || label.contains(event.relatedTarget)) return;
      window.clearTimeout(this.previewTimer);
      this.previewTimer = window.setTimeout(() => this.restoreSelectedVariantImage(), 80);
    }

    getProductContext() {
      const productInfo = this.closest("product-info");
      const mediaGallery = productInfo?.querySelector("media-gallery");
      const productDataElement = productInfo?.querySelector("#ProductData");
      if (!productInfo || !productDataElement) return null;
      try {
        return { mediaGallery, productData: JSON.parse(productDataElement.textContent) };
      } catch (error) {
        console.error("PeakStyle variant picker: invalid product data", error);
        return null;
      }
    }

    getSelectedOptionNames(overrideInput = null) {
      return Array.from(this.querySelectorAll("[data-picker-field]")).map((field) => {
        if (overrideInput && field.contains(overrideInput)) return overrideInput.value;
        if (field.dataset.pickerField === "select") return field.querySelector("select")?.value || null;
        return field.querySelector("input[type='radio']:checked")?.value || null;
      });
    }

    findVariant(productData, optionNames, hoveredInput = null) {
      const variants = productData?.variants || [];
      const exactMatch = variants.find((variant) => optionNames.every((optionName, index) => !optionName || variant.options[index] === optionName));
      if (exactMatch?.featured_media || exactMatch?.featured_image) return exactMatch;
      if (!hoveredInput) return exactMatch || null;

      const fields = Array.from(this.querySelectorAll("[data-picker-field]"));
      const hoveredPosition = fields.indexOf(hoveredInput.closest("[data-picker-field]"));
      return variants.find((variant) => variant.available && variant.options[hoveredPosition] === hoveredInput.value && (variant.featured_media || variant.featured_image)) ||
        variants.find((variant) => variant.options[hoveredPosition] === hoveredInput.value && (variant.featured_media || variant.featured_image)) ||
        exactMatch || null;
    }

    getVariantMediaId(variant) {
      return variant?.featured_media?.id || variant?.featured_image?.id || null;
    }

    previewVariantImage(input) {
      const context = this.getProductContext();
      if (!context?.mediaGallery || typeof context.mediaGallery.setActiveMedia !== "function") return;
      const variant = this.findVariant(context.productData, this.getSelectedOptionNames(input), input);
      const mediaId = this.getVariantMediaId(variant);
      if (mediaId) context.mediaGallery.setActiveMedia(mediaId);
    }

    restoreSelectedVariantImage() {
      const context = this.getProductContext();
      if (!context?.mediaGallery || typeof context.mediaGallery.setActiveMedia !== "function") return;
      const variant = this.findVariant(context.productData, this.getSelectedOptionNames());
      const mediaId = this.getVariantMediaId(variant);
      if (mediaId) context.mediaGallery.setActiveMedia(mediaId);
    }
  }

  customElements.define("variant-picker", VariantPicker);
}
